import { _ALIAS_SUFFIX, _NEW_LINE, _SPACE_CARD, _freeCards, _pathSeparator, charsMappingToURL } from "../components/constants";
import { normalizeString } from "../components/foo";


export function getNonEmptyLabels(labels:string[]){
  return labels.filter((label) => ![_SPACE_CARD, "", _NEW_LINE].includes(label.trim()));
}
export function cutTree(dict: {[key: string]: any}, prefixes: string[]): {[key: string]: string[]} {
    let result: {[key: string]: any} = {};
    for (let key in dict) {
        if (prefixes.some(prefix => {return key.startsWith(prefix+_pathSeparator) || key === prefix;})) {
            result[key] = dict[key];
        }
    }
    return result;
}

export function replaceKeyPrefixes(dict: {[key: string]: any}, oldPrefix: string, newPrefix: string): {[key: string]: any} {
    let newDict: {[key: string]: any} = {};
    for (let key in dict) {
        if (key.startsWith(oldPrefix)) {
            let newKey = newPrefix + key.slice(oldPrefix.length);
            newDict[newKey] = dict[key];
        } else {
            newDict[key] = dict[key];
        }
    }
    return newDict;
}


export function filterDict(inputDict: {[key: string]: string[]}, visibilityDict: {[key: string]: boolean}): {[key: string]: string[]} {
    // Create a copy of the input dictionary to avoid mutating the original
    let resultDict = {...inputDict};

    // Iterate over each key-value pair in the visibility dictionary
    for (let [key, value] of Object.entries(visibilityDict)) {
        // If the value is false, we need to remove this key from the result dictionary
        if (!value) {
            // Iterate over each key-value pair in the result dictionary
            for (let resultKey of Object.keys(resultDict)) {
                // If the resultKey contains the key, remove it
                if (
                    resultKey.startsWith(key+_pathSeparator) ||
                    resultKey.endsWith(_pathSeparator+key) ||
                    resultKey.includes(_pathSeparator+key+_pathSeparator)
                ) {
                    delete resultDict[resultKey];
                } else {
                    // Filter out any instances of the key from the list of values
                    resultDict[resultKey] = resultDict[resultKey].filter(item => item !== key);
                }
            }
        }
    }

    return resultDict;
}


export function removeTrueEntries(inputDict: {[key: string]: boolean}): {[key: string]: boolean} {
    let resultDict: {[key: string]: boolean} = {};
    for (let key in inputDict) {
        if (inputDict[key] !== true) {
            resultDict[key] = inputDict[key];
        }
    }
    return resultDict;
}

export function mergeImportedNodes(
    baseDict: { [key: string]: string[] },
    extraDict: { [key: string]: string[] },
    prefix: string,
    avoidDuplicates: boolean = false
  ): { [key: string]: string[] } {
    // Create a deep copy of baseDict
    const baseDictCopy = JSON.parse(JSON.stringify(baseDict));

    // Create a new dictionary with the same values but with keys prefixed
    const prefixedExtraDict: { [key: string]: string[] } = {};
    for (const key in extraDict) {
      prefixedExtraDict[prefix + key] = extraDict[key];
    }
  
    // Merge the new dictionary into the base dictionary copy
    for (const key in prefixedExtraDict) {
      if (key in baseDictCopy) {
        // If the key already exists in the base dictionary, merge the arrays
        // and remove duplicates using a Set
        if (avoidDuplicates) {
          baseDictCopy[key] = Array.from(new Set([...baseDictCopy[key], ...prefixedExtraDict[key]]));
        } else {
          baseDictCopy[key] = [...baseDictCopy[key], ...prefixedExtraDict[key]];
        }
      } else {
        // Otherwise, just add the new key-value pair to the base dictionary
        baseDictCopy[key] = prefixedExtraDict[key];
      }
    }
  
    return baseDictCopy;
  }

  export function mergeL2Image(
    baseDict: { [key: string]: string },
    extraDict: { [key: string]: string },
    cache: { [key: string]: string[] }
  ): { mergedDict: { [key: string]: string }, cache: { [key: string]: string[] } }
  {
    // Create a deep copy of the base dictionary and cache
    const mergedDict = JSON.parse(JSON.stringify(baseDict));
    const cacheCopy = JSON.parse(JSON.stringify(cache));
  
    // Add key-value pairs from the extra dictionary to the merged dictionary
    // only if they don't already exist in the base dictionary
    for (const key in extraDict) {
      if (!(key in mergedDict)) {
        mergedDict[key] = extraDict[key];
      }
  
      // Add key-value pairs from the extra dictionary to cache
      const normalizedKey = normalizeString(key);
      if (normalizedKey in cacheCopy) {
        // If the key already exists in cache, prepend the value from extraDict
        // and remove duplicates using a Set
        cacheCopy[normalizedKey] = Array.from(new Set([extraDict[key], ...cacheCopy[normalizedKey]]));
      } else {
        // Otherwise, add a new key-value pair to cache
        cacheCopy[normalizedKey] = [extraDict[key]];
      }
  
      // Ensure that each array in cache contains no more than five elements
      if (cacheCopy[normalizedKey].length > 5) {
        cacheCopy[normalizedKey] = cacheCopy[normalizedKey].slice(0, 5);
      }
    }
  
    return { mergedDict, cache: cacheCopy };
  }


  export function simpleDictUpdate(baseDict: {[key: string]: any}, extraDict: {[key: string]: any}): {[key: string]: any} {
    // Create a deep copy of the base dictionary
    let baseDictCopy = JSON.parse(JSON.stringify(baseDict));
  
    for (let key in extraDict) {
        baseDictCopy[key] = extraDict[key];
    }
    return baseDictCopy;
  }

  type StringListDictionary = { [key: string]: string[] };

  export function mergeCachedDicts(dict1: StringListDictionary, dict2: StringListDictionary): StringListDictionary {
      return {
          ...dict1,
          ...Object.fromEntries(
              Object.keys(dict2).map(key => [key, (dict1[key] || []).concat(dict2[key])])
          ),
      };
  }
  
  export function cleanCachedDicts(dict: StringListDictionary): StringListDictionary {
    const cleanedDict: StringListDictionary = {};

    for (const key in dict) {
        const uniqueValues = Array.from(new Set(dict[key]))
            .filter(value => value?.startsWith('data:image') && value.includes('base64,'));

        if (uniqueValues.length > 0) {
            cleanedDict[key] = uniqueValues;
        }
    }

    return cleanedDict;
}

  export function checkIfAllNodesHaveAtLeastOneChild(
    basePath: string,
    labels: string[],
    nodes: {[key: string]: any[]}): boolean {
    for (let label of labels) {
        if (nodes[basePath+_pathSeparator+label] === undefined || nodes[basePath+_pathSeparator+label].length === 0) {
            return false;
        }
    }
    return true;
  }

export function mapNodesValuesChars(nodes: { [key: string]: string[] }): { [key: string]: string[] } {
    return Object.entries(nodes).reduce((result, [key, value]) => {
      result[key] = value.map(charsMappingToURL);
      return result;
  }, {} as { [key: string]: string[] }); 
}
 
export function mapKeysInL2Dict(dict: { [key: string]: any }): { [key: string]: any } {
  return Object.entries(dict).reduce((result, [key, value]) => {
      result[charsMappingToURL(key)] = value;
      return result;
  }, {} as { [key: string]: string[] });
} 


export function findFirstDictByPrefixAndSuffix(dict: {[key: string]: string[]}, prefix: string, suffix: string): {key: string, value: string[]} | null {
  for (let key in dict) {
      if (key.startsWith(prefix) && key.endsWith(suffix)) {
          return { key: key, value: dict[key] };
      }
  }
  return null;
}


export function getFirstFreeCards(nodes: {[key:string] : string[]}, isSubscribed:boolean): string[] {
  if(isSubscribed) {
    return [];
  }
  
  // Convert the nodes into an array of entries
  let entries = Object.entries(nodes);
  
  // Sort the entries by the length of their keys (shortest first)
  entries.sort((a, b) => a[0].length - b[0].length);
  
  // Flatten the sorted entries into a single array of values
  let values = entries.flatMap(entry => entry[1]);
  
  return values.slice(0, _freeCards);
}


export function filterDictByKeys(obj: {[key: string]: any}, keys: string[]) {
    let filteredObj: {[key: string]: any} = {};
    for (let key of keys) {
        if (key in obj) {
            filteredObj[key] = obj[key];
        }
    }
    return filteredObj;
}


export function nodesCorrection(
  nodes: { [key: string]: string[] },
  setNodes: (nodes: { [key: string]: string[] }) => void,
  nodePath: string | undefined
) {
  if (nodePath == undefined) {
    return;
  }
  let newNodes = { ...nodes };

  // Delete initial _NEW_LINE and null elements
  let nodePathValue = nodes[nodePath];
  if (nodePathValue !== undefined && nodePathValue.length > 0) {
    let firstNonNewLineIndex = nodePathValue.findIndex(value => value !== _NEW_LINE && value !== null);
    if (firstNonNewLineIndex > 0) {
      
      newNodes[nodePath] = nodePathValue.slice(firstNonNewLineIndex);
      
    }
  

  // Delete consecutive _NEW_LINE elements
  const filteredLabels = [];
  for (const label of newNodes[nodePath]) {
    if (label !== _NEW_LINE || filteredLabels.length === 0 || filteredLabels[filteredLabels.length - 1] !== _NEW_LINE) {
      filteredLabels.push(label);
    }
  }
  newNodes[nodePath] = filteredLabels;
  }

  setNodes(newNodes);
}

/**
 * Checks if there is at least one occurrence of _NEW_LINE in the selected labels
 * and if each _NEW_LINE is flanked by non-new-line labels.
 *
 * @param selectedLabels - An object with keys as labels and values as arrays of positions.
 * @returns true if _NEW_LINE is present and flanked by non-new-line labels, otherwise false.
 */
export function hasNewLineWithNonNewLineChars(selectedLabels: { [key: string]: number[] }): boolean {
  if (!(_NEW_LINE in selectedLabels) || selectedLabels[_NEW_LINE].length === 0) {
    return false;
  }

  // Create a sorted array of all positions
  const allPositions = Object.values(selectedLabels).flat().sort((a, b) => a - b);

  return selectedLabels[_NEW_LINE].some(newLinePos => {
    const newLineIndex = allPositions.indexOf(newLinePos);
    const prevPos = allPositions[newLineIndex - 1];
    const nextPos = allPositions[newLineIndex + 1];

    // Check if the positions before and after _NEW_LINE are not _NEW_LINE themselves
    return prevPos !== undefined && nextPos !== undefined &&
           !selectedLabels[_NEW_LINE].includes(prevPos) &&
           !selectedLabels[_NEW_LINE].includes(nextPos);
  });
}


/**
 * Deletes occurrences of _NEW_LINE from each node's label array in the nodes dictionary,
 * but only at positions specified in selectedLabels.
 *
 * @param nodes - A dictionary where each key is a node and its value is an array of labels.
 * @param selectedLabels - An object with keys as labels and values as arrays of positions.
 * @returns A new dictionary with specific _NEW_LINE occurrences removed from each label array.
 */
export function deleteSelectedNewLinesFromNodes(nodes: { [key: string]: string[] }, selectedLabels: { [key: string]: number[] }): { [key: string]: string[] } {
  const updatedNodes = { ...nodes };
  const newLinePositions = new Set(selectedLabels[_NEW_LINE] || []);

  Object.keys(updatedNodes).forEach(nodeKey => {
    let labelIndex = 0;
    updatedNodes[nodeKey] = updatedNodes[nodeKey].filter(label => {
      if (label === _NEW_LINE && newLinePositions.has(labelIndex)) {
        labelIndex++;
        return false;
      }
      labelIndex++;
      return true;
    });
  });

  return updatedNodes;
}


/**
 * Shuffles the label groups in an array.
 * 
 * @param labelGroups - An array of label groups to be shuffled.
 * @returns A new array with the label groups shuffled.
 */
export function shuffleLabelGroups(labelGroups: string[][]) {
  // Function to shuffle an array
  const shuffle = (array:string[]) => {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  }

  // Map over labelGroups, shuffle each group
  const shuffledLabelGroups = labelGroups.map(group => shuffle([...group]));

  return shuffledLabelGroups;
}


export function getLabelRootName(label: string): string {
  // Get first occurrence of _ALIAS_SUFFIX and return the substring before it
  return label.split(_ALIAS_SUFFIX)[0];
}